import React, { useState, useRef, useCallback } from 'react';
import { auth } from '../firebase';

const UPLOAD_HANDLER_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-upload-handler';

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'm4v', '3gp', 'ts', 'hevc'];
const VIDEO_MAX_MB = 40;

const IGNORED_FILES = ['.ds_store', 'thumbs.db', 'desktop.ini', '.thumbnail'];

function FileUpload({ gido, quota, onUploadComplete }) {

  const [dragging, setDragging]     = useState(false);
  const [uploads, setUploads]       = useState([]);
  const [error, setError]           = useState('');
  const fileInputRef                = useRef(null);

  const maxFileMb = quota?.max_file_size_mb || 500;

  // ── Validate file ────────────────────────────────────────────
  const sanitizeFilename = (filename) => {
    return filename
      .replace(/[<>:"/\\|?*\x00-\x1f]/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '')
      .replace(/\s+/g, '_')
      .trim()
      .substring(0, 200);
  };

  const validateFile = (file) => {
    const name = file.name.toLowerCase();
    const ext  = name.includes('.') ? name.split('.').pop() : '';
    const mb   = file.size / (1024 * 1024);

    if (IGNORED_FILES.includes(name)) {
      return { valid: false, reason: 'System file ignored' };
    }

    if (VIDEO_EXTENSIONS.includes(ext) && mb > VIDEO_MAX_MB) {
      return { valid: false, reason: `Video limited to ${VIDEO_MAX_MB}MB (yours: ${mb.toFixed(1)}MB)` };
    }

    if (mb > maxFileMb) {
      return { valid: false, reason: `File too large: ${mb.toFixed(1)}MB (max ${maxFileMb}MB)` };
    }

    return { valid: true };
  };

  // ── Upload single file ────────────────────────────────────────
  const uploadFile = async (file) => {
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Add to uploads list
    setUploads(prev => [...prev, {
      id:       fileId,
      name:     file.name,
      size:     file.size,
      status:   'preparing',
      progress: 0,
      error:    null,
    }]);

    const updateUpload = (updates) => {
      setUploads(prev => prev.map(u => u.id === fileId ? { ...u, ...updates } : u));
    };

    try {
      // Step 1: Get signed URL from backend
      updateUpload({ status: 'getting_url', progress: 5 });

      const idToken  = await auth.currentUser.getIdToken();
      const response = await fetch(UPLOAD_HANDLER_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          id_token:   idToken,
          filename:   sanitizeFilename(file.name),
          mime_type:  file.type || 'application/octet-stream',
          size_bytes: file.size,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get upload URL');
      }

      // Step 2: Upload directly to GCS
      updateUpload({ status: 'uploading', progress: 10 });

      await uploadToGCS(
        data.upload_url,
        file,
        file.type || 'application/octet-stream',
        (progress) => updateUpload({ progress: 10 + (progress * 0.85) })
      );

      // Step 3: Notify backend to process file
      updateUpload({ status: 'uploading', progress: 97 });

      try {
        const notifyToken = await auth.currentUser.getIdToken();
        await fetch('https://europe-west1-dbos-core-prod.cloudfunctions.net/smartdata-notify-upload', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            id_token:   notifyToken,
            gcs_path:   data.gcs_path,
            bucket:     data.bucket,
            filename:   file.name,
            mime_type:  file.type || 'application/octet-stream',
            size_bytes: file.size,
          }),
        });
      } catch (notifyErr) {
        console.warn('Notify failed silently:', notifyErr);
      }

      // Step 4: Done!
      updateUpload({ status: 'complete', progress: 100 });

      // Notify parent to refresh file list
      if (onUploadComplete) {
        setTimeout(() => onUploadComplete(), 2000);
      }

    } catch (err) {
      console.error('Upload error:', err);
      updateUpload({ status: 'error', error: err.message });
    }
  };

  // ── Upload to GCS via signed URL ─────────────────────────────
  const uploadToGCS = (signedUrl, file, mimeType, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
      xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', mimeType);
      xhr.send(file);
    });
  };

  // ── Handle files ─────────────────────────────────────────────
  const handleFiles = useCallback(async (files) => {
    setError('');
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setError(`${file.name}: ${validation.reason}`);
        continue;
      }
      await uploadFile(file);
    }
  }, [quota]);

  // ── Drag & Drop handlers ─────────────────────────────────────
  const handleDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };
  const handleDrop      = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e) => handleFiles(e.target.files);

  // ── Clear completed ───────────────────────────────────────────
  const clearCompleted = () => {
    setUploads(prev => prev.filter(u => u.status !== 'complete'));
  };

  // ── Format size ───────────────────────────────────────────────
  const formatSize = (bytes) => {
    if (bytes < 1024)        return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const statusIcon = (status) => {
    if (status === 'complete')     return '✅';
    if (status === 'error')        return '❌';
    if (status === 'uploading')    return '⬆️';
    if (status === 'preparing' ||
        status === 'getting_url')  return '⏳';
    return '📄';
  };

  const hasActive    = uploads.some(u => u.status === 'uploading' || u.status === 'preparing' || u.status === 'getting_url');
  const hasCompleted = uploads.some(u => u.status === 'complete');

  return (
    <div className="file-upload">

      {/* Drop zone */}
      <div
        className={`drop-zone ${dragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <div className="drop-zone-icon">📥</div>
        <div className="drop-zone-title">
          {dragging ? 'Drop files here!' : 'Drag & drop files here'}
        </div>
        <div className="drop-zone-subtitle">
          or click to browse
        </div>
        <div className="drop-zone-limits">
          Max {maxFileMb}MB per file · Videos max {VIDEO_MAX_MB}MB · All file types accepted
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="upload-error">
          ⚠️ {error}
          <button onClick={() => setError('')} className="error-close">✕</button>
        </div>
      )}

      {/* Upload queue */}
      {uploads.length > 0 && (
        <div className="upload-queue">
          <div className="upload-queue-header">
            <span>Uploads ({uploads.length})</span>
            {hasCompleted && !hasActive && (
              <button onClick={clearCompleted} className="clear-btn">Clear completed</button>
            )}
          </div>

          {uploads.map(upload => (
            <div key={upload.id} className={`upload-item ${upload.status}`}>
              <div className="upload-item-icon">{statusIcon(upload.status)}</div>
              <div className="upload-item-info">
                <div className="upload-item-name">{upload.name}</div>
                <div className="upload-item-meta">
                  {formatSize(upload.size)}
                  {upload.error && <span className="upload-item-error"> · {upload.error}</span>}
                  {upload.status === 'complete' && <span className="upload-item-success"> · Uploaded ✓</span>}
                </div>
                {(upload.status === 'uploading' || upload.status === 'getting_url') && (
                  <div className="upload-progress-bar">
                    <div
                      className="upload-progress-fill"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
              </div>
              <div className="upload-item-pct">
                {upload.status === 'complete' ? '100%' :
                 upload.status === 'error'    ? 'Failed' :
                 `${Math.round(upload.progress)}%`}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default FileUpload;